'use client';

import React, { useState } from 'react';
import { CreateSupport, GetAllOutlets } from '@/services/support.api';
import { toast } from 'react-toastify';
import Banner from './banner';
import { supportImg } from '@/assets/images';
import Modal from './Modal';
import Link from 'next/link';
import { GrStatusGood } from 'react-icons/gr';

export default function SupportForm() {
  const [openModal, setOpenModal] = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [loadingOutlets, setLoadingOutlets] = useState(true);

  React.useEffect(() => {
    const fetchOutlets = async () => {
      try {
        setLoadingOutlets(true);
        const data = await GetAllOutlets();
        setOutlets(data || []);
      } catch (error) {
        console.log(error);
        toast.error('Failed to load outlets. Please refresh the page.');
      } finally {
        setLoadingOutlets(false);
      }
    };
    fetchOutlets();
  }, []);

  const [formData, setFormData] = useState<{
    name: string;
    emailAddress: string;
    description: string;
    phoneNumber: string;
    category: string;
    outlet: string;
    file: File | null;
  }>({
    name: '',
    emailAddress: '',
    description: '',
    phoneNumber: '',
    category: '',
    outlet: '',
    file: null,
  });
  const [loading, setLoading] = useState(false);
  const categoryOptions = [
    'Select Category',
    'Customer Service',
    'Quality of product',
    'Environment and Ambience',
    'Payment and Pricing',
    'Online Orders and Sales',
    'Other',
  ];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!validTypes.includes(selectedFile.type)) {
        toast.error('Invalid file type. Please upload a JPEG, PNG, or PDF.');
        return;
      }

      if (selectedFile.size > maxSize) {
        toast.error('File size exceeds the 5MB limit.');
        return;
      }

      setFormData((prevState) => ({
        ...prevState,
        file: selectedFile,
      }));
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    if (
      !formData.name ||
      !formData.emailAddress ||
      !formData.description ||
      !formData.phoneNumber ||
      !formData.category ||
      !formData.outlet
    ) {
      setErrorMsg('Please fill in all required fields');
      return;
    }
    setLoading(true);
    e.preventDefault();
    try {
      await CreateSupport(formData);
      setLoading(false);
      setFormData({
        name: '',
        emailAddress: '',
        description: '',
        phoneNumber: '',
        category: '',
        outlet: '',
        file: null,
      });
      setOpenModal(true);
    } catch (error: any) {
      setLoading(false);
      setErrorMsg(
        error.message ||
          'An error occurred while submitting this form. Please try again.'
      );
      console.log(error);
    }
  };

  return (
    <>
      {/* <button
        onClick={() => setOpenModal(true)}
        className="flex gap-2 justify-between text-crunchies-red items-center bg-crunchies-white shadow-lg p-3 rounded-lg"
      >
        <div className="flex gap-2 items-center">
          <BiSupport className="w-6 lg:hidden" />
          <div>Support</div>
        </div>
      </button> */}

      {/* <Modal
        closeModal={() => setOpenModal(false)}
        openModal={openModal}
        modalTitle="Support Request"
      > */}

      <Modal
        closeModal={() => setOpenModal(false)}
        openModal={openModal}
        shouldCloseOnOverlayClick={true}
        modalTitle=""
      >
        <div className="grid gap-4 w-4/5 lg:w-3/5 m-auto">
          <GrStatusGood className=" m-auto text-green-500" size={100} />
          <h1 className="text-xl font-semibold text-center">
            Thank you for your feedback! Our team will be in touch with you soon
          </h1>
          <div className="flex m-auto items-center mt-10 mb-5">
            <Link
              href="/"
              className="bg-crunchies-red text-crunchies-white py-4 px-6 rounded text-center font-semibold"
            >
              {' '}
              Okay{' '}
            </Link>
          </div>
        </div>
      </Modal>

      <div className="w-[80%] m-auto mb-10">
        <Banner
          title="Customer Feedback & Support Form"
          description="This is the customer feedback channel of Crunchies Fried Chicken. Kindly ensure to fill in all required details."
          img={supportImg}
        />
      </div>
      {/* <div className="grid gap-4 w-4/5 lg:w-3/5 m-auto">
        <h1 className="text-2xl">Customer Feedback & Support Form</h1>
        <p>Please fill out the form below</p>
      </div> */}
      <form
        onSubmit={handleSubmit}
        className="grid gap-4 w-4/5 lg:w-3/5 m-auto mt-6 mb-20"
      >
        <div>
          <label
            htmlFor="name"
            className="block text-base font-medium text-gray-700"
          >
            Your name <span className="text-red-500 font-bold">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className="flex w-full py-2 px-10 border outline-none my-1 pl-5 font-poppinsLight focus:border-gray-700 focus:ring-gray-700 rounded-lg"
            required
          />
        </div>

        <div>
          <label
            htmlFor="emailAddress"
            className="block text-base font-medium text-gray-700"
          >
            Your email address <span className="text-red-500 font-bold">*</span>
          </label>
          <input
            type="email"
            id="emailAddress"
            name="emailAddress"
            value={formData.emailAddress}
            onChange={handleInputChange}
            className="flex w-full py-2 px-10 border outline-none my-1 pl-5 font-poppinsLight focus:border-gray-700 focus:ring-gray-700 rounded-lg"
            required
          />
        </div>

        <div>
          <label
            htmlFor="name"
            className="block text-base font-medium text-gray-700"
          >
            Your phone number <span className="text-red-500 font-bold">*</span>
          </label>
          <input
            type="text"
            id="phoneNumber"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleInputChange}
            className="flex w-full py-2 px-10 border outline-none my-1 pl-5 font-poppinsLight focus:border-gray-700 focus:ring-gray-700 rounded-lg"
            required
          />
        </div>

        <div>
          <label
            htmlFor="category"
            className="block text-base font-medium text-gray-700"
          >
            Choose feedback category{' '}
            <span className="text-red-500 font-bold">*</span>
          </label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            className="flex w-full py-2 px-10 border outline-none my-1 pl-5 font-poppinsLight focus:border-gray-700 focus:ring-gray-700 rounded-lg"
            required
          >
            {categoryOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="description"
            className="block text-base font-medium text-gray-700"
          >
            Kindly share your feedback/request/complaint{' '}
            <span className="text-red-500 font-bold">*</span>
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={4}
            className="flex w-full py-2 px-10 border outline-none my-1 pl-5 font-poppinsLight resize-none focus:border-gray-700 focus:ring-gray-700 rounded-lg"
            required
          ></textarea>
        </div>

        <div>
          <label
            htmlFor="outlet"
            className="block text-base font-medium text-gray-700"
          >
            Choose the engaged outlet{' '}
            <span className="text-red-500 font-bold">*</span>
          </label>
          <select
            id="outlet"
            name="outlet"
            value={formData.outlet}
            onChange={handleInputChange}
            className="flex w-full py-2 px-10 border outline-none my-1 pl-5 font-poppinsLight focus:border-gray-700 focus:ring-gray-700 rounded-lg"
            required
            disabled={loadingOutlets}
          >
            <option value="">
              {loadingOutlets ? 'Loading outlets...' : 'Select Outlet'}
            </option>
            {outlets.length > 0
              ? outlets.map((outlet: any) => (
                  <option key={outlet._id} value={outlet.name}>
                    {outlet.name}
                  </option>
                ))
              : !loadingOutlets && (
                  <option value="" disabled>
                    No outlets available
                  </option>
                )}
          </select>
        </div>

        <div>
          <label
            htmlFor="file"
            className="block text-base font-medium text-gray-700"
          >
            Attach any supporting documents or images for your feedback.{' '}
            <span className="italic text-sm">Max file size is 5MB</span>
          </label>
          <input
            id="file"
            name="file"
            type="file"
            onChange={handleFileChange}
            className="flex w-full py-2 px-10 border outline-none my-1 pl-5 font-poppinsLight focus:border-gray-700 focus:ring-gray-700 rounded-lg"
          />
        </div>

        <div>
          {errorMsg && <p className="text-red-500 text-sm">{errorMsg}</p>}
        </div>

        <div className="w-full flex justify-center">
          <button
            type="submit"
            className=" w-1/2  text-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-crunchies-red hover:bg-crunchies-red focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-crunchies-red"
          >
            {loading ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </form>
    </>
  );
}
